import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { patientApi, patientErr } from "@/lib/patientApi";
import { loadSnap } from "@/components/BuyRelaxButton";
import { Heart, ArrowLeft, Receipt, MusicNotes, ClockCountdown } from "@phosphor-icons/react";

const rupiah = (n) => "Rp" + (n || 0).toLocaleString("id-ID");
const STATUS = {
  pending: { t: "Menunggu Pembayaran", c: "bg-amber-100 text-amber-700" },
  paid: { t: "Lunas", c: "bg-emerald-100 text-emerald-700" },
  expired: { t: "Kedaluwarsa", c: "bg-slate-100 text-slate-500" },
  failed: { t: "Gagal", c: "bg-rose-100 text-rose-600" },
  cancelled: { t: "Dibatalkan", c: "bg-slate-100 text-slate-500" },
  refunded: { t: "Refund (akses dicabut)", c: "bg-rose-100 text-rose-600" },
};

function Countdown({ iso }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(iso).getTime() - Date.now();
      if (ms <= 0) { setLeft("00:00"); return; }
      const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
      setLeft(`${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [iso]);
  return <span data-testid="riwayat-countdown" className="inline-flex items-center gap-1 font-bold text-amber-700"><ClockCountdown size={14} weight="fill" /> {left}</span>;
}

export default function RiwayatPembelian() {
  const nav = useNavigate();
  const [orders, setOrders] = useState(undefined);
  const [open, setOpen] = useState(null);
  const PAGE = 10;
  const [shown, setShown] = useState(PAGE);

  const load = useCallback(() => {
    patientApi.get("/patient/relax/orders").then((r) => setOrders(r.data.orders))
      .catch((e) => { if (e?.response?.status === 401) { localStorage.removeItem("patient_token"); nav("/pasien/masuk"); } else { setOrders([]); toast.error(patientErr(e)); } });
  }, [nav]);

  useEffect(() => {
    document.title = "Riwayat Pembelian — HalloGBS";
    if (!localStorage.getItem("patient_token")) { nav("/pasien/masuk"); return; }
    load();
  }, [nav, load]);

  const resume = async (o) => {
    try {
      const { data } = await patientApi.post(`/patient/relax/orders/${o.order_no}/resume`);
      await loadSnap(data.client_key, data.is_production);
      if (!window.snap) { toast.error("Gagal memuat Midtrans."); return; }
      window.snap.pay(data.snap_token, {
        onSuccess: () => poll(o.order_no), onPending: () => poll(o.order_no),
        onError: () => toast.error("Pembayaran gagal."), onClose: () => load(),
      });
    } catch (err) { toast.error(patientErr(err)); load(); }
  };

  const poll = async (orderNo, tries = 20) => {
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await patientApi.get(`/patient/relax/orders/${orderNo}`);
        if (data.status === "paid") { toast.success("Pembayaran berhasil!"); load(); setOpen(data); return; }
        if (["failed", "expired", "cancelled"].includes(data.status)) { toast.error("Pembayaran tidak selesai."); load(); return; }
      } catch { /* keep polling */ }
    }
    load();
  };

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="riwayat-pembelian">
      <header className="border-b border-rose-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          <a href="/akun-saya" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><ArrowLeft size={16} weight="bold" /> Akun</a>
          <span className="flex items-center gap-1.5 font-heading text-base font-extrabold text-[#4C1D95]"><Heart size={18} weight="fill" className="text-[#DB2777]" /> HalloGBS</span>
        </div>
      </header>

      <main className="mx-auto max-w-md space-y-4 px-4 py-6">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-[#4C1D95]"><Receipt size={24} weight="duotone" className="text-[#DB2777]" /> Riwayat Pembelian</h1>

        {orders === undefined && <p className="py-10 text-center text-sm text-slate-400">Memuat…</p>}
        {orders && orders.length === 0 && <p data-testid="riwayat-empty" className="rounded-3xl border border-dashed border-pink-200 py-12 text-center text-sm text-slate-500">Belum ada pembelian.</p>}

        {orders && orders.slice(0, shown).map((o) => {
          const s = STATUS[o.status] || { t: o.status, c: "bg-slate-100 text-slate-600" };
          return (
            <button key={o.order_no} data-testid={`riwayat-item-${o.order_no}`} onClick={() => setOpen(o)}
              className="flex w-full items-center gap-3 rounded-2xl border border-pink-100 bg-white p-4 text-left">
              <span className="min-w-0 flex-1">
                <span className="block truncate font-heading text-sm font-extrabold text-[#1E1B4B]">{o.title}</span>
                <span className="mt-0.5 block text-[11px] text-slate-400">{o.order_no} · {o.item_type === "bundle" ? "Bundle" : "Produk"}</span>
                <span className="mt-1 flex items-center gap-2 text-xs">
                  <span className="font-bold text-[#7C4DE0]">{rupiah(o.total)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${s.c}`}>{s.t}</span>
                  {o.needs_admin_review && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">Refund — Perlu Review</span>}
                </span>
              </span>
              {o.status === "pending" && o.expires_at && <Countdown iso={o.expires_at} />}
            </button>
          );
        })}
        {orders && orders.length > shown && (
          <button data-testid="riwayat-more" onClick={() => setShown((n) => n + PAGE)} className="w-full rounded-xl border border-pink-200 py-2 text-sm font-bold text-[#7C4DE0]">Muat lebih banyak</button>
        )}
      </main>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="riwayat-detail" className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#DB2777]">{open.item_type === "bundle" ? "Bundle" : "Produk"}</div>
            <h2 className="mt-0.5 font-heading text-lg font-extrabold text-[#1E1B4B]">{open.title}</h2>
            <div className="mt-1 text-xs text-slate-400">No. Pesanan: {open.order_no}</div>

            <div className="mt-4 space-y-2 rounded-2xl bg-[#FAF7FF] p-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-bold">{rupiah(open.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-heading font-extrabold text-[#7C4DE0]">{rupiah(open.total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span>
                <span className="font-bold">{(STATUS[open.status] || {}).t || open.status}</span></div>
              {open.status === "pending" && open.expires_at && (
                <div className="flex justify-between"><span className="text-slate-500">Batas waktu</span><Countdown iso={open.expires_at} /></div>
              )}
              {open.needs_admin_review && <div className="rounded-lg bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700">Refund sebagian — Perlu Review Admin. Akses Anda belum berubah.</div>}
              {open.status === "refunded" && <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold text-rose-600">Pembelian direfund penuh — akses materi dari pesanan ini telah dicabut.</div>}
            </div>

            {open.status === "pending" && (
              <button data-testid="riwayat-continue" onClick={() => resume(open)} className="mt-4 w-full rounded-2xl bg-gradient-to-r from-[#F472B6] to-[#DB2777] py-3.5 font-heading text-base font-extrabold text-white">Lanjutkan Pembayaran</button>
            )}
            {open.status === "paid" && (
              <button data-testid="riwayat-listen" onClick={() => nav("/koleksi-saya")} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#7C4DE0] py-3.5 font-heading text-base font-extrabold text-white"><MusicNotes size={20} weight="fill" /> Dengarkan di Koleksi Saya</button>
            )}
            <button onClick={() => setOpen(null)} className="mt-2 w-full rounded-2xl border border-slate-200 py-2.5 text-sm font-bold text-slate-500">Tutup</button>
          </div>
        </div>
      )}
    </div>
  );
}
